import { useMemo, useState, useEffect } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, CardBody, Input, Sheet, Spinner, Stub, Textarea, Toast } from "@/components/ui"
import type { ScheduleOut } from "@/lib/types"
import { CalendarClockIcon, PlusIcon, Trash2Icon, ToggleLeftIcon, ToggleRightIcon } from "lucide-react"

export const Route = createFileRoute("/_app/schedules")({
  component: SchedulesPage,
  // From the bell: "you asked this N times — make it a schedule?" lands here
  // with the request pre-filled and the suggestion id to dismiss on save.
  validateSearch: (q: Record<string, unknown>): { suggest?: string; sid?: string } => ({
    suggest: typeof q.suggest === "string" ? q.suggest : undefined,
    sid: typeof q.sid === "string" ? q.sid : undefined,
  }),
})

/** Mirrors SUPPORTED_KINDS in app/agent/tools/create_schedule.py. */
type Kind = "reminder" | "research_brief" | "agent_run"
type Cadence = "daily" | "weekdays" | "weekly" | "hourly" | "every_4_hours" | "monthly" | "custom"

const CADENCES: Cadence[] = ["daily", "weekdays", "weekly", "hourly", "every_4_hours", "monthly", "custom"]

function buildCron(c: Cadence, hhmm: string, weekday: number, custom: string): string {
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10))
  const hh = Number.isFinite(h) ? h : 9
  const mm = Number.isFinite(m) ? m : 0
  switch (c) {
    case "daily": return `${mm} ${hh} * * *`
    case "weekdays": return `${mm} ${hh} * * 1-5`
    case "weekly": return `${mm} ${hh} * * ${weekday}`
    case "hourly": return `${mm} * * * *`
    case "every_4_hours": return `${mm} */4 * * *`
    case "monthly": return `${mm} ${hh} 1 * *`
    default: return custom.trim()
  }
}

/** Best-effort human label for a stored cron (the common shapes we write). */
function describeCron(expr: string, t: (k: string, v?: Record<string, string | number>) => string): string {
  const parts = expr.trim().split(/\s+/)
  if (parts.length !== 5) return expr
  const [m, h, dom, , dow] = parts
  const time = /^\d+$/.test(h) && /^\d+$/.test(m) ? `${h.padStart(2, "0")}:${m.padStart(2, "0")}` : null
  if (h === "*" && dom === "*" && dow === "*") return t("sched.cadence.hourly")
  if (h === "*/4" && dom === "*" && dow === "*") return t("sched.cadence.every_4_hours")
  if (time && dom === "1" && dow === "*") return `${t("sched.cadence.monthly")} ${t("sched.at")} ${time}`
  if (time && dom === "*" && dow === "*") return `${t("sched.cadence.daily")} ${t("sched.at")} ${time}`
  if (time && dom === "*" && (dow === "1-5" || dow === "MON-FRI")) return `${t("sched.cadence.weekdays")} ${t("sched.at")} ${time}`
  if (time && dom === "*" && /^[0-6]$/.test(dow)) return `${t(`day.${dow}`)} ${t("sched.at")} ${time}`
  return expr
}

function summary(s: ScheduleOut): string {
  const p = s.payload || {}
  return String(p.text ?? p.query ?? p.goal ?? "")
}

function SchedulesPage() {
  const t = useT()
  const me = auth.me()
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ["schedules", me?.id],
    queryFn: () => api<ScheduleOut[]>("/api/dashboard/schedules", { query: { user_id: me?.id } }),
    enabled: !!me?.id,
  })
  const { suggest, sid } = Route.useSearch()
  const [adding, setAdding] = useState(!!suggest)

  const toggle = useMutation({
    mutationFn: (s: ScheduleOut) =>
      api(`/api/dashboard/schedules/${s.id}`, { method: "PATCH", body: { active: !s.active }, query: { user_id: me?.id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  })
  const del = useMutation({
    mutationFn: (id: string) => api(`/api/dashboard/schedules/${id}`, { method: "DELETE", query: { user_id: me?.id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t("sched.title")}</h1>
          <p className="text-[13px] text-muted-foreground mt-1.5">{t("sched.subtitle")}</p>
        </div>
        <Button onClick={() => setAdding(true)}><PlusIcon className="h-4 w-4" /> {t("sched.new")}</Button>
      </div>

      {list.isLoading && <Spinner />}
      {list.data?.length === 0 && <Stub title={t("sched.empty")} hint={t("sched.emptyHint")} />}

      <div className="grid gap-2">
        {list.data?.map((s) => (
          <Card key={s.id}>
            <CardBody className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <CalendarClockIcon className="mt-0.5 h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.name || summary(s) || s.kind}</div>
                  {summary(s) && s.name && <div className="text-xs text-muted-foreground truncate">{summary(s)}</div>}
                  <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <Badge variant="muted">{t(`sched.kind.${s.kind}`) === `sched.kind.${s.kind}` ? s.kind : t(`sched.kind.${s.kind}`)}</Badge>
                    <span title={s.cron_expr}>{describeCron(s.cron_expr, t)}</span>
                    {!!(s.payload as { followup?: { after_hours?: number } })?.followup?.after_hours && (
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.08em] text-violet">
                        +{t("sched.followupBadge", { h: (s.payload as { followup: { after_hours: number } }).followup.after_hours })}
                      </span>
                    )}
                    <span>· {s.tz}</span>
                    {s.next_fire_at && s.active && (
                      <span>· {t("sched.next")}: {new Date(s.next_fire_at).toLocaleString()}</span>
                    )}
                  </div>
                  {s.last_error && (
                    <div className="mt-1 text-[11px] text-destructive truncate" title={s.last_error}>
                      {t("sched.lastError")}: {s.last_error}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => toggle.mutate(s)} title={s.active ? "On" : "Off"}>
                  {s.active ? <ToggleRightIcon className="h-5 w-5 text-[hsl(var(--ok))]" /> : <ToggleLeftIcon className="h-5 w-5 text-muted-foreground" />}
                </Button>
                <Button variant="ghost" size="icon" title={t("sched.delete")} onClick={() => {
                  if (window.confirm(`${t("sched.delete")}: "${s.name || summary(s)}"?`)) del.mutate(s.id)
                }}>
                  <Trash2Icon className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      <NewScheduleSheet
        open={adding}
        initial={suggest ? { kind: "agent_run", body: suggest } : undefined}
        onSaved={() => { if (sid) void api("/api/me/suggestions/dismiss", { method: "POST", body: { id: sid } }).catch(() => undefined) }}
        onClose={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["schedules"] }) }}
      />
    </div>
  )
}

function NewScheduleSheet({ open, onClose, initial, onSaved }: {
  open: boolean; onClose: () => void
  initial?: { kind: Kind; body: string }
  onSaved?: () => void
}) {
  const t = useT()
  const me = auth.me()
  const [name, setName] = useState("")
  const [kind, setKind] = useState<Kind>("reminder")
  const [body, setBody] = useState("")
  const [cadence, setCadence] = useState<Cadence>("daily")
  const [time, setTime] = useState("09:00")
  const [weekday, setWeekday] = useState(1)
  const [custom, setCustom] = useState("0 9 * * 1-5")
  const [tz, setTz] = useState(me?.tz && me.tz !== "UTC" ? me.tz : (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Follow-up: N hours after each run, Lucy checks something and only
  // speaks if it is still pending ("tell me if X didn't reply").
  const [followOn, setFollowOn] = useState(false)
  const [followHours, setFollowHours] = useState(48)
  const [followCheck, setFollowCheck] = useState("")

  useEffect(() => {
    if (open && initial) { setKind(initial.kind); setBody(initial.body) }
  }, [open, initial])
  const cron = useMemo(() => buildCron(cadence, time, weekday, custom), [cadence, time, weekday, custom])
  const needsTime = cadence === "daily" || cadence === "weekdays" || cadence === "weekly" || cadence === "monthly"
  const bodyLabel = kind === "reminder" ? t("sched.reminderText") : kind === "research_brief" ? t("sched.query") : t("sched.goal")

  async function save() {
    if (!me?.id || !body.trim()) return
    setBusy(true); setErr(null)
    const payload: Record<string, unknown> =
      kind === "reminder" ? { text: body.trim() }
      : kind === "research_brief" ? { query: body.trim(), name: name.trim() || undefined }
      : { goal: body.trim() }
    if (followOn && followCheck.trim() && followHours > 0) {
      payload.followup = { after_hours: Math.round(followHours), check: followCheck.trim() }
    }
    try {
      await api("/api/dashboard/schedules", {
        method: "POST",
        body: { user_id: me.id, name: name.trim() || body.trim().slice(0, 60), kind, cron_expr: cron, tz, payload },
      })
      setName(""); setBody("")
      onSaved?.()
      onClose()
    } catch (e) {
      setErr((e as { detail?: string }).detail || (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("sched.new")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t("sched.cancel")}</Button>
          <Button onClick={save} disabled={busy || !body.trim() || !cron}>{busy ? <Spinner /> : null}{t("sched.create")}</Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <Field label={t("sched.what")}>
          <div className="grid gap-1.5">
            {(["reminder", "research_brief", "agent_run"] as Kind[]).map((k) => (
              <label key={k} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 ${kind === k ? "border-primary/50 bg-primary/10" : "border-border bg-foreground/[0.04]"}`}>
                <input type="radio" name="kind" className="accent-[hsl(var(--primary))]" checked={kind === k} onChange={() => setKind(k)} />
                <span>{t(`sched.kind.${k}`)}</span>
              </label>
            ))}
          </div>
        </Field>
        <Field label={bodyLabel}>
          <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} autoFocus />
        </Field>
        <Field label={t("sched.name")}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.optional")} />
        </Field>
        <Field label={t("sched.when")}>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value as Cadence)}
              className="rounded-xl border border-border bg-black/25 px-3 py-2 text-sm text-foreground [&>option]:bg-card"
            >
              {CADENCES.map((c) => <option key={c} value={c}>{t(`sched.cadence.${c}`)}</option>)}
            </select>
            {cadence === "weekly" && (
              <select
                value={weekday}
                onChange={(e) => setWeekday(parseInt(e.target.value, 10))}
                className="rounded-xl border border-border bg-black/25 px-3 py-2 text-sm text-foreground [&>option]:bg-card"
              >
                {[1, 2, 3, 4, 5, 6, 0].map((d) => <option key={d} value={d}>{t(`day.${d}`)}</option>)}
              </select>
            )}
            {needsTime && (
              <>
                <span className="text-muted-foreground">{t("sched.at")}</span>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-28" />
              </>
            )}
          </div>
          {cadence === "custom" && (
            <div className="mt-2">
              <Input value={custom} onChange={(e) => setCustom(e.target.value)} className="font-mono" placeholder="0 9 * * 1-5" />
              <p className="text-[10px] text-muted-foreground mt-1">{t("sched.custom")}: <code>min hour day month weekday</code></p>
            </div>
          )}
          <p className="mt-1.5 text-[11px] text-muted-foreground">{describeCron(cron, t)} <span className="font-mono opacity-60">({cron})</span></p>
        </Field>
        <Field label={t("sched.followup")}>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" className="accent-[hsl(var(--primary))]" checked={followOn} onChange={(e) => setFollowOn(e.target.checked)} />
            <span>{t("sched.followupOn")}</span>
          </label>
          {followOn && (
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <Input type="number" min={1} max={336} value={followHours} onChange={(e) => setFollowHours(parseInt(e.target.value || "0", 10))} className="w-24" />
                <span className="text-muted-foreground">{t("sched.followupHours")}</span>
              </div>
              <Textarea rows={2} value={followCheck} onChange={(e) => setFollowCheck(e.target.value)} placeholder={t("sched.followupCheckHint")} />
              <p className="text-[11px] text-muted-foreground">{t("sched.followupNote")}</p>
            </div>
          )}
        </Field>
        <Field label={t("sched.timezone")}><Input value={tz} onChange={(e) => setTz(e.target.value)} className="font-mono" /></Field>
        {err && <Toast variant="destructive">{err}</Toast>}
      </div>
    </Sheet>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  )
}
